import { groupBy, nonNullable } from 'lib/array/arrayUtils'
import { createSelector } from 'reselect'
import offerPageURL from 'lib/offer/offerPageURL'
import { getLabels } from 'checkout/lib/utils/accommodation/label'
import { getLocationString } from 'checkout/lib/utils/accommodation/location'
import { excludeNullOrUndefined } from 'checkout/utils'
import { getBedbankRateKey } from 'actions/BedbankOfferActions'
import { buildBedbankRateFromEnquirySession } from 'checkout/lib/utils/bedbank/buildBedbankRateFromEnquirySession'
import { getBedbankItemView } from 'checkout/lib/utils/bedbank/view'
import { getFullOffers } from 'selectors/offerSelectors'
import getBedbankItems from 'checkout/selectors/view/getBedbankItems'
import { isBedbankOffer } from 'lib/offer/offerTypes'
import getFlightBundledItemIds from './getFlightBundledItemIds'
import { diffInDays } from 'lib/datetime/dateUtils'
import { pluralizeToString } from 'lib/string/pluralize'
import { findPostPurchaseCheckout } from 'lib/checkout/checkoutUtils'
import { selectIsLebtItemPoliciesEnabled } from 'businessTraveller/selectors/itemPolicy/selectIsLebtItemPoliciesEnabled'
import { selectIsLebtHotelLoyaltyEnabled } from 'businessTraveller/selectors/selectIsLebtHotelLoyaltyEnabled'
import { selectIsLebtSabreHotelsEnabled } from 'businessTraveller/selectors/selectIsLebtSabreHotelsEnabled'
import type { HotelLoyaltyFeatureFlags } from 'lib/hotels/hotelLoyaltyEligibility'

const getBedbankItemViews = createSelector(
  (state: App.State) => getBedbankItems(state),
  (state: App.State) => getFullOffers(state),
  (state: App.State) => state.offer.bedbankOfferRates,
  // When the item policies flag is on, drop the legacy frontend-calculated business credits in favour of the cart evaluation
  (state: App.State) => selectIsLebtItemPoliciesEnabled(state) ? undefined : state.businessTraveller.offersCredits,
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => state.checkout.cart.existingOrder,
  (state: App.State) => state.checkout.sessions,
  (state: App.State) => getFlightBundledItemIds(state),
  (state: App.State) => state.businessTraveller.employees,
  (state: App.State) => selectIsLebtHotelLoyaltyEnabled(state),
  (state: App.State) => selectIsLebtSabreHotelsEnabled(state),
  (
    cartItems,
    offers,
    bedbankRatesByOffer,
    offerCredits,
    postPurchase,
    existingOrder,
    sessions,
    bundledItemIds,
    employees,
    isHotelLoyaltyEnabled,
    sabreHotelsEnabled,
  ): App.WithDataStatus<Array<App.Checkout.BedbankAccommodationOfferView>> => {
    const hotelLoyaltyFeatureFlags: HotelLoyaltyFeatureFlags = {
      isHotelLoyaltyEnabled,
      sabreHotelsEnabled,
    }
    const employeesMap = employees?.status === 'success' ? employees.all : undefined
    // Each item = 1 room, but we want to process the same dates/offer as a single view
    const itemsByBookingKey = groupBy(cartItems, item => `${item.offerId}-${item.checkIn}-${item.checkOut}`)
    const itemGroups = itemsByBookingKey.values().map(items => ({
      checkIn: items[0].checkIn,
      checkOut: items[0].checkOut,
      offerId: items[0].offerId,
      items,
    })).toArray()

    const views = itemGroups.map((itemGroup): App.WithDataStatus<App.Checkout.BedbankAccommodationOfferView | undefined> => {
      const { offerId, checkIn, checkOut, items } = itemGroup
      const offer = offers[offerId]

      if (!isBedbankOffer(offer)) {
        return {
          hasRequiredData: false,
          data: undefined,
        }
      }

      const itemsBySessionId = groupBy(items, (item) => item.sessionId ?? 'none')

      let bedbankOfferRate: App.BedbankRate | undefined
      const itemViewsWithStatusesAndBedGroups = items.map(item => {
        const groupData = itemsBySessionId.get(item.sessionId ?? 'none') ?? []
        const occupancy = groupData.length > 0 ? groupData.map(item => item.occupancy) : [item.occupancy]
        const rateKey = getBedbankRateKey(occupancy, checkIn, checkOut)
        const bedbankOfferRates = bedbankRatesByOffer[offerId]?.[rateKey]
        const bedbankRatesFetched = !!bedbankRatesByOffer[offerId]
        const session = sessions[item.sessionId ?? 'none']?.data ?? null
        bedbankOfferRate = bedbankOfferRates?.find(rate => rate.id === item.roomRateId && rate.isFlightBundle === item.isFlightBundle)
          ?? (session ? buildBedbankRateFromEnquirySession(session, offer, item) : undefined)

        return {
          bedGroups: bedbankOfferRate?.bedGroups ?? [],
          itemViewsWithStatuses: getBedbankItemView({
            item,
            luxPlusLPPEnabled: false,
            session,
            offer,
            rate: bedbankOfferRate,
            offerCredits,
            postPurchase,
            existingOrder,
            ratesFetched: bedbankRatesFetched,
            employees: employeesMap,
            hotelLoyaltyFeatureFlags,
          }),
        }
      })

      const duration = diffInDays(new Date(checkIn), new Date(checkOut))

      const bundledWithTour = items.some(item => !!item.bundledItemIds?.length)
      const designation = 'accommodation'

      const view: App.Checkout.BedbankAccommodationOfferView = {
        offerId,
        offer,
        offerPageUrl: offerPageURL(offer),
        ...getLabels(offer),
        bedGroups: itemViewsWithStatusesAndBedGroups.flatMap(i => i.bedGroups),
        confidenceLabels: [],
        duration,
        startDate: checkIn,
        endDate: checkOut,
        image: offer?.image,
        location: getLocationString(offer),
        locationCountry: offer.property.address.countryName,
        locationCountryCode: offer.property.address.countryCode,
        occupancy: items.map(item => item.occupancy).filter(excludeNullOrUndefined),
        offerType: offer?.type,
        reservationType: 'instant_booking',
        urgencyLabels: [],
        saleUnit: offer?.saleUnitLong,
        durationLabel: pluralizeToString('night', duration),
        offerLoaded: true,
        itemViews: itemViewsWithStatusesAndBedGroups.map(i => i.itemViewsWithStatuses.data),
        propertyTimezone: offer.property.timezone,
        bundledWithFlightsOnly: false,
        bundledWithTour,
        designation,
        isBundled: items.some(item => bundledItemIds.has(item.itemId)),
        hasMobilePromotion: !!bedbankOfferRate?.mobilePromotion,
        propertyId: offer.property.id,
      }

      return {
        data: view,
        hasRequiredData: itemViewsWithStatusesAndBedGroups.every(itemView => itemView.itemViewsWithStatuses.hasRequiredData),
      }
    })

    return {
      data: nonNullable(views.map(view => view.data)),
      hasRequiredData: views.every(view => view.hasRequiredData),
    }
  },
)

export default getBedbankItemViews
